<template>
  <div class="d-flex flex-column row-gap-3" data-locator="features">
    <div class="text--medium text--extra-bold">
      {{ $t('project.project-settings.ProjectSettingsFeatures.title') }}
    </div>

    <!-- Excel download format settings -->
    <AppCard
      v-if="isCurrentUserPm"
      data-locator="excel-download-settings"
      elevation="0"
      class="border"
    >
      <div class="d-flex flex-column flex-sm-row align-start align-sm-center">
        <div class="d-flex flex-column flex-grow-1 pr-0 pr-sm-6">
          <p class="text--medium text--bold">
            {{ $t('project.project-settings.ProjectSettingsFeatures.excelDownloadSettings.title') }}
          </p>
          <p class="mb-0 text--small">
            {{ excelDownloadSettingsEnabled
              ? $t('project.project-settings.ProjectSettingsFeatures.excelDownloadSettings.descriptionOn')
              : $t('project.project-settings.ProjectSettingsFeatures.excelDownloadSettings.descriptionOff')
            }}
          </p>
        </div>

        <div class="d-flex justify-end flex-shrink-0 mt-1 mt-sm-0">
          <AppTooltip location="top">
            <template #activator="{ props }">
              <div v-bind="props">
                <app-toggle
                  :value="excelDownloadSettingsEnabled"
                  color="darkblue-500"
                  iconColor="darkblue-500"
                  iconSize="xxxs"
                  hide-details
                  class="excel-settings-toggle"
                  data-locator="excel-download-settings-toggle"
                />
              </div>
            </template>
            <span style="display: inline-block; width: 320px">
              {{ $t('project.project-settings.ProjectSettingsFeatures.excelDownloadSettings.toggleTooltip') }}
            </span>
          </AppTooltip>
        </div>
      </div>

      <template v-if="excelDownloadSettingsEnabled">
        <!-- Warning banner -->
        <app-alert-in-page
          variant="warning"
          :dismissible="false"
          :has-left-border="false"
          class="mt-4"
        >
          <span class="text--small">
            <span class="text--bold">{{ $t('project.project-settings.ProjectSettingsFeatures.excelDownloadSettings.warningLabel') }}</span>
            {{ $t('project.project-settings.ProjectSettingsFeatures.excelDownloadSettings.warningBody') }}
          </span>
        </app-alert-in-page>

        <!-- Excel format selection -->
        <div v-if="roomRightsPending" class="mt-4 d-flex justify-center">
          <app-loader />
        </div>
        <div v-else-if="groupsWithWatermarkAndDownload.length > 0" class="mt-4">
          <div
            v-for="group in groupsWithWatermarkAndDownload"
            :key="group.id"
            class="d-flex align-center py-2"
            style="gap: 24px"
          >
            <span class="text--small"  style="width: 160px;">{{ group.name }}</span>
            <AppSelect
              :data-locator="`excel-format-select-${group.id}`"
              :model-value="group.excelDownloadFormat || 'pdf'"
              :items="excelFormatOptions"
              no-label
              style="max-width: 220px"
              @update:model-value="onUpdateGroupExcelFormat(group.id, $event)"
            />
          </div>
        </div>
      </template>
    </AppCard>

    <!-- QA limit -->
    <AppCard
      v-if="isCurrentUserPm"
      data-locator="qa-limit-settings"
      class="mb-4"
    >
      <div class="d-flex flex-column flex-sm-row align-start align-sm-center">
        <div class="d-flex flex-column flex-grow-1 pr-0 pr-sm-6">
          <p class="text--medium text--bold">
            {{ $t('project.project-settings.ProjectSettingsFeatures.qaLimitSettings.title') }}
          </p>
          <p class="mb-0 text--small">
            {{ $t('project.project-settings.ProjectSettingsFeatures.qaLimitSettings.description') }}
          </p>
        </div>

        <div class="d-flex justify-end mt-1 mt-sm-0">
          <app-toggle
            :value="localQaLimitEnabled"
            :disabled="postRoomQuestionsLimitPending"
            color="darkblue-500"
            iconColor="darkblue-500"
            iconSize="xxxs"
            hide-details
            data-locator="qa-limit-toggle"
            @input="onToggleQaLimitEnabled"
          />
        </div>
      </div>
      <template v-if="localQaLimitEnabled">
        <div class="d-flex flex-row align-start mt-4" style="gap: 24px">
          <AppTextField
            :model-value="localQaLimitCount"
            :label="$t('project.project-settings.ProjectSettingsFeatures.qaLimitSettings.questionsCount')"
            type="number"
            :min="qaMinCount"
            :error="!!(qaLimitCountTouched && qaLimitCountError)"
            style="max-width: 250px"
            data-locator="qa-limit-count-input"
            @update:model-value="localQaLimitCount = $event !== '' ? Number($event) : null"
            @blur="onQaLimitCountBlur"
          />
          <AppSelect
            v-model="localQaLimitFrequency"
            :label="$t('project.project-settings.ProjectSettingsFeatures.qaLimitSettings.frequency')"
            :items="frequencyOptions"
            style="max-width: 200px"
            data-locator="qa-limit-frequency-select"
          />
          <div class="d-flex align-end pb-1">
            <app-button
              type="outlined"
              variant="neutral"
              :disabled="!isQaLimitCountValid || postRoomQuestionsLimitPending"
              :loading="postRoomQuestionsLimitPending"
              data-locator="qa-limit-save-button"
              @click="onSaveQaLimit"
            >
              {{ $t('common.register') }}
            </app-button>
          </div>
        </div>
        <p v-if="qaLimitCountTouched && qaLimitCountError"
          class="my-2 text-caption text-error"
          data-locator="qa-limit-count-error"
        >
          {{ qaLimitCountError }}
        </p>
        <p v-if="localQaLimitEnabled && qaLimitResetHint" class="mb-0 mt-2 text-caption">
          {{ qaLimitResetHint }}
        </p>
      </template>
    </AppCard>

    <!-- QA Authors -->
    <AppCard
      v-if="isCurrentUserPm"
      id="hideQaAuthorsCard"
      data-locator="hide-qa-authors-settings"
      elevation="0"
      class="border"
    >
      <div class="d-flex flex-column flex-sm-row align-start align-sm-center">
        <div class="d-flex flex-column flex-grow-1 pr-0 pr-sm-6">
          <p class="text--medium text--bold">
            {{ $t('project.project-settings.ProjectSettingsFeatures.hideQaAuthors.title') }}
          </p>
          <p class="mb-0 text--small">
            {{ $t('project.project-settings.ProjectSettingsFeatures.hideQaAuthors.description') }}
          </p>
        </div>

        <div class="d-flex justify-end mt-1 mt-sm-0">
          <app-toggle
            :value="localHideQaAuthors"
            :loading="toggleHideAuthorsLoading"
            color="darkblue-500"
            iconColor="darkblue-500"
            iconSize="xxxs"
            hide-details
            data-locator="hide-qa-authors-toggle"
            @input="onToggleHideQaAuthors"
          />
        </div>
      </div>
    </AppCard>

    <!-- NDA -->
    <AppCard 
      data-locator="nda-settings"
      elevation="0"
      class="border"
    >
      <div class="d-flex flex-column flex-sm-row align-start align-sm-center">
        <div class="d-flex flex-column flex-grow-1 pr-0 pr-sm-6">
          <div class="d-flex align-center">
            <p class="text--medium text--bold">
              {{ $t('project.project-settings.ProjectSettingsFeatures.nda.title') }}
            </p>
            <template v-if="hasNda">
              <app-icon-badge
                color="success"
                class="mb-2 ml-3 mr-2"
                :bordered="false"
                dot
              />
              <p class="text-success text--small text--bold">
                {{ $t("project.project-settings.ProjectSettingsFeatures.nda.activated") }}
              </p>
            </template>
          </div>

          <p class="mb-0 text--small">
            {{ $t('project.project-settings.ProjectSettingsFeatures.nda.ndaLabel') }}
          </p>
        </div>

        <div class="d-flex justify-end mt-1 mt-sm-0">
          <app-button
            v-if="!hasNda"
            data-locator="activate-button"
            type="outlined"
            variant="neutral"
            :disabled="toggleNdaLoading"
            :loading="toggleNdaLoading"
            @click="isNdaActivationDialogOpen = true"
          >
            {{ $t('common.activate') }}...
          </app-button>
          <app-button
            v-else
            data-locator="delete-button"
            type="outlined"
            variant="neutral"
            :disabled="toggleNdaLoading"
            :loading="toggleNdaLoading"
            @click="isNdaDeletionDialogOpen = true"
          >
            {{ $t("common.delete") }}
          </app-button>
        </div>
      </div>
    </AppCard>

    <NdaDialog
      v-if="isNdaActivationDialogOpen"
      :mnemo="mnemo"
      @close="isNdaActivationDialogOpen = false"
      @nda-added="isNdaAddedSignDialogOpen = true"
    />
    <NdaDeletionDialog
      v-if="isNdaDeletionDialogOpen"
      @close="isNdaDeletionDialogOpen = false"
      @confirm="prepareToggleNdaDataroomAccess"
    />
    <NdaAddedSignDialog
      v-if="isNdaAddedSignDialogOpen"
      :mnemo="mnemo"
      @close="isNdaAddedSignDialogOpen = false"
    />
  </div>
</template>

<script lang="ts">
import dayjs from 'dayjs'
import { defineComponent, computed, ComputedRef, ref, watch } from 'vue'
import { useStore } from 'vuex'

import AppCard from '@/common/AppCard.vue'
import AppLoader from '@/common/AppLoader.vue'
import AppSelect from '@/common/AppSelect.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import AppTextField from '@/common/AppTextField.vue'
import { toast } from '@/common/utils/toast'
import i18n from '@/plugins/i18n'
import NdaAddedSignDialog from '@/project/nda/dialogs/NdaAddedSignDialog.vue'
import NdaDeletionDialog from '@/project/nda/dialogs/NdaDeletionDialog.vue'
import NdaDialog from '@/project/nda/dialogs/NdaDialog.vue'
import {
  PATCH_ROOM,
  PATCH_GROUP_EXCEL_DOWNLOAD_FORMAT,
  POST_ROOM_QUESTIONS_LIMIT,
} from '@/store/modules/room/action_types'
import { SET_CURRENT_ROOM } from '@/store/modules/room/mutation_types'

import { QaLimitFrequency } from './enums'

export default defineComponent({
  name: 'ProjectSettingsDataroomFeatures',
  components: {
    NdaDialog,
    NdaDeletionDialog,
    NdaAddedSignDialog,
    AppCard,
    AppLoader,
    AppSelect,
    AppTextField,
    AppTooltip,
  },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const store = useStore()
    const currentRoom = computed(() => store.state.room.currentRoom)
    const isCurrentUserPm = computed(() => store.getters['room/isCurrentUserPm'])

    const toggleHideAuthorsLoading = ref(false)
    const localHideQaAuthors = ref<boolean>(false)
    const localQaLimitEnabled = ref<boolean>(false)
    const localQaLimitCount = ref<number | null>(null)
    const localQaLimitFrequency = ref<QaLimitFrequency>(QaLimitFrequency.NONE)
    const qaLimitCountTouched = ref<boolean>(false)

    const groupHasWatermarkAndDownload = (g: any): boolean => {
      if (g.isPm) return false
      if (g.displayWatermark && g.canDownload) return true
      return g.members?.some((m: any) => m.displayWatermark && m.canDownload) ?? false
    }

    const excelDownloadSettingsEnabled = computed(() => {
      return (store.state.room.roomRights?.groups ?? []).some(groupHasWatermarkAndDownload)
    })

    const allGroups = computed(() => {
      return store.state.room.roomRights?.groups ?? []
    })

    const groupsWithWatermarkAndDownload = computed(() => {
      return allGroups.value.filter(groupHasWatermarkAndDownload)
    })

    const roomRightsPending = computed(() => store.state.room.roomRightsPending)

    const excelFormatOptions = computed(() => [
      {
        title: i18n.global.t('project.project-settings.ProjectSettingsFeatures.excelDownloadSettings.formatPdf'),
        value: 'pdf',
      },
      {
        title: i18n.global.t('project.project-settings.ProjectSettingsFeatures.excelDownloadSettings.formatOriginal'),
        value: 'original',
      },
    ])

    const frequencyOptions = computed(() => [
      { 
        title: i18n.global.t('project.project-settings.ProjectSettingsFeatures.qaLimitSettings.frequencyOptions.never'),
        value: QaLimitFrequency.NONE,
      },
      {
        title: i18n.global.t('project.project-settings.ProjectSettingsFeatures.qaLimitSettings.frequencyOptions.perDay'),
        value: QaLimitFrequency.DAILY,
      },
      {
        title: i18n.global.t('project.project-settings.ProjectSettingsFeatures.qaLimitSettings.frequencyOptions.perWeek'),
        value: QaLimitFrequency.WEEKLY,
      },
      {
        title: i18n.global.t('project.project-settings.ProjectSettingsFeatures.qaLimitSettings.frequencyOptions.perMonth'),
        value: QaLimitFrequency.MONTHLY,
      },
    ])

    const qaCurrentCount = computed(() => currentRoom.value?.countsForRoom?.questions ?? 0)

    const qaMinCount = computed(() => qaCurrentCount.value || 1)

    const qaLimitCountError = computed((): string => {
      if (
        (localQaLimitCount.value === null || localQaLimitCount.value === undefined || String(localQaLimitCount.value) === '') ||
        localQaLimitCount.value < 1) {
        return i18n.global.t('project.project-settings.ProjectSettingsFeatures.qaLimitSettings.questionsCountBelowZeroError')
      }
      if (localQaLimitCount.value < qaCurrentCount.value) {
        return i18n.global.t('project.project-settings.ProjectSettingsFeatures.qaLimitSettings.questionsCountBelowCurrentError')
      }
      return ''
    })

    const isQaLimitCountValid = computed(() => qaLimitCountError.value.length === 0)

    const qaLimitResetHint = computed((): string => {
      let resetDate
      const freqFromApi = currentRoom.value.dataroomInfo.questionsLimitFrequency as QaLimitFrequency
      const localFreq = localQaLimitFrequency.value
      const resetDateFromApi = currentRoom.value?.dataroomInfo?.questionsLimitResetDate
      if (!resetDateFromApi || (localFreq !== freqFromApi)) {
        
        if (localFreq === QaLimitFrequency.NONE) return ''
        resetDate = dayjs()
        if (localFreq === QaLimitFrequency.DAILY) resetDate = resetDate.add(1, 'day')
        else if (localFreq === QaLimitFrequency.WEEKLY) resetDate = resetDate.add(1, 'week')
        // Using 30 days as a month duration for the reset hint, as it was a product decision to
        // avoid confusion with the exact reset date (e.g. if the month has 28, 29, 30 or 31 days) 
        else if (localFreq === QaLimitFrequency.MONTHLY) resetDate = resetDate.add(30, 'day')
      } else {
        resetDate = dayjs.utc(currentRoom.value.dataroomInfo.questionsLimitResetDate)
      }
      return i18n.global.t(
        'project.project-settings.ProjectSettingsFeatures.qaLimitSettings.questionsCountResetHint',
        {
          date: resetDate.format('DD/MM/YYYY'), time: '00:00',
        },
      )
    })

    const postRoomQuestionsLimitPending = computed(() => store.state.room.postRoomQuestionsLimitPending)

    watch(
      () => currentRoom.value?.hideQaAuthors,
      (v) => {
        localHideQaAuthors.value = Boolean(v)
      },
      { immediate: true },
    )

    watch(
      () => currentRoom.value?.dataroomInfo,
      (dataroomInfo) => {
        const limit = dataroomInfo.questionsLimit ?? null
        localQaLimitCount.value = limit
        localQaLimitFrequency.value = (dataroomInfo.questionsLimitFrequency as QaLimitFrequency) ?? QaLimitFrequency.NONE
        localQaLimitEnabled.value = limit !== null
        qaLimitCountTouched.value = false
      },
      {
        immediate: true,
        deep: true,
      },
    )

    const onToggleHideQaAuthors = async (value: boolean): Promise<void> => {
      if (toggleHideAuthorsLoading.value) return
      if (value === Boolean(currentRoom.value?.hideQaAuthors)) return
  
      try {
        toggleHideAuthorsLoading.value = true

        await store.dispatch(`room/${PATCH_ROOM}`, {
          mnemo: props.mnemo,
          data: {
            hideQaAuthors: value,
          },
        })

        if (currentRoom.value) {
          currentRoom.value.hideQaAuthors = value
          store.commit(`room/${SET_CURRENT_ROOM}`, currentRoom.value)
        }

        toast.success(
          value
            ? i18n.global.t('project.project-settings.ProjectSettingsFeatures.hideQaAuthors.successOn')
            : i18n.global.t('project.project-settings.ProjectSettingsFeatures.hideQaAuthors.successOff'),
        )
      } catch (e) {
        console.error(e)
        toast.error(i18n.global.t('project.project-settings.ProjectSettingsFeatures.hideQaAuthors.error'))
        localHideQaAuthors.value = Boolean(currentRoom.value?.hideQaAuthors)
      } finally {
        toggleHideAuthorsLoading.value = false
      }
    }

    const onToggleQaLimitEnabled = async (value: boolean): Promise<void> => {
      if (value === localQaLimitEnabled.value) return

      if (value) {
        localQaLimitEnabled.value = true
        return
      }

      try {
        await store.dispatch(`room/${POST_ROOM_QUESTIONS_LIMIT}`, {
          mnemo: props.mnemo,
          data: {
            enable: false,
          },
        })

        localQaLimitEnabled.value = false

        if (currentRoom.value) {
          currentRoom.value.dataroomInfo.questionsLimit = null
          currentRoom.value.dataroomInfo.questionsLimitFrequency = null
          currentRoom.value.dataroomInfo.questionsLimitResetDate = null
          store.commit(`room/${SET_CURRENT_ROOM}`, currentRoom.value)
        }

        toast.success(i18n.global.t('common.saveConfirmation'))
      } catch (e) {
        console.error(e)
        toast.error(i18n.global.t('common.saveError'))
      }
    }

    const onSaveQaLimit = async (): Promise<void> => {
      if (!isQaLimitCountValid.value) return

      try {
        let reply = await store.dispatch(`room/${POST_ROOM_QUESTIONS_LIMIT}`, {
          mnemo: props.mnemo,
          data: {
            enable: true,
            limit: localQaLimitCount.value,
            frequency: localQaLimitFrequency.value,
          },
        })

        toast.success(i18n.global.t('common.saveConfirmation'))

        if (currentRoom.value) {
          currentRoom.value.dataroomInfo.questionsLimit = localQaLimitCount.value
          currentRoom.value.dataroomInfo.questionsLimitFrequency = localQaLimitFrequency.value
          currentRoom.value.dataroomInfo.questionsLimitResetDate = reply.data.questionsLimitResetDate
          store.commit(`room/${SET_CURRENT_ROOM}`, currentRoom.value)
        }
      } catch (e) {
        console.error(e)
        toast.error(i18n.global.t('common.saveError'))
      }
    }

    const hasNda: ComputedRef<boolean | undefined> = computed(() => currentRoom.value?.hasNda)
    const isNdaActivationDialogOpen = ref(false)
    const isNdaDeletionDialogOpen = ref(false)
    const isNdaAddedSignDialogOpen = ref(false)

    const onUpdateGroupExcelFormat = async (groupId: number, format: string): Promise<void> => {
      try {
        await store.dispatch(`room/${PATCH_GROUP_EXCEL_DOWNLOAD_FORMAT}`, {
          mnemo: props.mnemo,
          groupId,
          data: { excelDownloadFormat: format },
        })
        toast.success(i18n.global.t('project.project-settings.ProjectSettingsFeatures.excelDownloadSettings.successFormatUpdate'))
      } catch (e) {
        console.error(e)
        toast.error(i18n.global.t('project.project-settings.ProjectSettingsFeatures.excelDownloadSettings.error'))
      }
    }
    const toggleNdaLoading = ref(false)

    const prepareToggleNdaDataroomAccess = async (): Promise<void> => {
      try {
        toggleNdaLoading.value = true
        const newValue = !hasNda.value
        await store.dispatch(`room/${PATCH_ROOM}`, {
          mnemo: props.mnemo,
          data: {
            optNda: newValue,
          },
        })
        if (currentRoom.value) {
          currentRoom.value.hasNda = !hasNda.value
          store.commit(`room/${SET_CURRENT_ROOM}`, currentRoom.value)
        }
        if (newValue) {
          toast.success(i18n.global.t('project.project-settings.ProjectSettingsFeatures.nda.ndaActivationSuccess'))
        } else {
          toast.success(i18n.global.t('project.project-settings.ProjectSettingsFeatures.nda.ndaDeactivationSuccess'))
        }
      } catch (error) {
        console.error(error)
        toast.error(i18n.global.t('project.project-settings.ProjectSettingsFeatures.nda.sndaActivationError'))
      } finally {
        toggleNdaLoading.value = false
      }
    }

    return {
      isCurrentUserPm,
      localHideQaAuthors,
      localQaLimitEnabled,
      localQaLimitCount,
      localQaLimitFrequency,
      frequencyOptions,
      qaLimitCountError,
      isQaLimitCountValid,
      qaCurrentCount,
      qaMinCount,
      qaLimitCountTouched,
      qaLimitResetHint,
      onQaLimitCountBlur: () => { qaLimitCountTouched.value = true },
      postRoomQuestionsLimitPending,
      toggleHideAuthorsLoading,
      onToggleHideQaAuthors,
      onToggleQaLimitEnabled,
      onSaveQaLimit,
      hasNda,
      toggleNdaLoading,
      isNdaActivationDialogOpen,
      isNdaDeletionDialogOpen,
      isNdaAddedSignDialogOpen,
      prepareToggleNdaDataroomAccess,
      allGroups,
      groupsWithWatermarkAndDownload,
      excelDownloadSettingsEnabled,
      roomRightsPending,
      excelFormatOptions,
      onUpdateGroupExcelFormat,
    }
  },
})
</script>

<style lang="scss" scoped>
// Excel settings toggle is read-only
// block interaction without the visual disabled (grey) state
.excel-settings-toggle {
  pointer-events: none;
  cursor: default;
}

.AppToggle {
  :deep(.v-switch__track) {
    height: 20px;
    min-width: 36px;
    opacity: 1;
    &.bg-primary {
      opacity: 1;
    }
  }
  :deep(.v-selection-control__input) {
    width: 30px;
    height: 30px;
  }
  :deep(.v-switch__thumb) {
    height: 15px;
    width: 15px;
  }
  :deep(.v-selection-control) {
    min-height: 20px;
  }
  :deep(.v-selection-control__wrapper) {
    height: 20px;
  }
  :deep(*) {
    .v-selection-control__input {
      transform: translateX(-7px) !important;
    }
    .v-selection-control--dirty .v-selection-control__input {
      transform: translateX(7px) !important;
    }
  }
}
</style>
